import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { auth } from '../firebase';
import CustomSelect from './ui/CustomSelect';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000';
const PHONE_DIGITS_PATTERN = /^\d{10}$/;
const COUNTRY_CODE = '+91';

const qualificationOptions = [
  '12th',
  'Diploma',
  'Bachelor',
  'Master',
  'PhD'
];

const genderOptions = ['Male', 'Female', 'Other'];

const normalizePhone = (value) => value.replace(/\D/g, '');

function GoogleProfileCompletionModal({ user, userProfile, onComplete }) {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    gender: '',
    qualification: '',
    city: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setFormData({
      fullName: userProfile?.fullName || user?.displayName || '',
      phone: userProfile?.phone || '',
      gender: userProfile?.gender || '',
      qualification: userProfile?.qualification || '',
      city: userProfile?.city || ''
    });
  }, [user, userProfile]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: name === 'phone' ? normalizePhone(value) : value
    }));
  };

  const validateForm = () => {
    const trimmedName = formData.fullName.trim();
    const trimmedCity = formData.city.trim();

    if (!trimmedName || !formData.phone || !formData.gender || !formData.qualification || !trimmedCity) {
      return 'Please fill all required fields.';
    }

    if (!PHONE_DIGITS_PATTERN.test(formData.phone)) {
      return 'Please enter a valid phone number with 10 to 15 digits.';
    }

    return '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const token = await auth.currentUser?.getIdToken();

      const response = await axios.post(`${API_BASE_URL}/api/save_user_profile`, {
        uid: user.uid,
        fullName: formData.fullName.trim(),
        email: user.email || userProfile?.email || '',
        phone: formData.phone,
        city: formData.city.trim(),
        gender: formData.gender,
        qualification: formData.qualification,
        provider: 'google',
        requireCompleteProfile: true
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      await onComplete(response.data.profile);
    } catch (submitError) {
      console.error('Failed to complete Google profile:', submitError);
      setError(
        submitError.response?.data?.message ||
        submitError.response?.data?.error ||
        'We could not save your details right now. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center bg-slate-950/60 px-3 py-4 backdrop-blur-sm sm:items-center sm:px-6 sm:py-6">
      <div className="relative flex max-h-[calc(100vh-2rem)] w-full max-w-[400px] flex-col overflow-hidden rounded-[22px] border border-white/70 bg-white shadow-[0_32px_80px_-36px_rgba(15,23,42,0.5)] animate-[fadeIn_220ms_ease-out] sm:max-h-[min(700px,calc(100vh-3rem))] sm:rounded-3xl">
        <div className="relative border-b border-slate-100 px-4 pb-2.5 pt-3 sm:px-5 sm:pb-3.5 sm:pt-4.5">
          <h2 className="text-[20px] font-semibold leading-tight tracking-tight text-slate-900 sm:text-[27px]">
            Complete Your Profile
          </h2>
          <p className="mt-0.5 text-sm leading-5 text-slate-500">
            Just a few details to get started.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="relative flex-1 overflow-y-auto px-3.5 pb-4 pt-3 sm:px-4.5 sm:pb-5 sm:pt-3.5">
          {error && (
            <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-600">
              {error}
            </div>
          )}

          <div className="space-y-3">
            <div>
              <label htmlFor="google-fullName" className="mb-1.5 block text-sm font-medium text-slate-700">
                Name
              </label>
              <input
                id="google-fullName"
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                maxLength={80}
                required
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 transition-all focus:border-[#9a51a0] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#9a51a0]/20 sm:rounded-xl"
              />
            </div>

            <div>
              <label htmlFor="google-phone" className="mb-1.5 block text-sm font-medium text-slate-700">
                Phone Number
              </label>
              <div className="flex overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 transition-all focus-within:border-[#9a51a0] focus-within:bg-white focus-within:ring-2 focus-within:ring-[#9a51a0]/20 sm:rounded-xl">
                <div className="flex items-center border-r border-slate-200 px-4 text-sm font-semibold text-slate-600">
                  {COUNTRY_CODE}
                </div>
                <input
                  id="google-phone"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="Enter 10 digit number"
                  required
                  className="w-full bg-transparent px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none"
                />
              </div>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="min-w-0">
              <label htmlFor="google-gender" className="mb-1.5 block text-sm font-medium text-slate-700">
                Gender
              </label>
              <CustomSelect
                id="google-gender"
                name="gender"
                value={formData.gender}
                options={genderOptions}
                placeholder="Select Gender"
                onChange={handleChange}
              />
            </div>

            <div className="min-w-0">
              <label htmlFor="google-qualification" className="mb-1.5 block text-sm font-medium text-slate-700">
                Qualification
              </label>
              <CustomSelect
                id="google-qualification"
                name="qualification"
                value={formData.qualification}
                options={qualificationOptions}
                placeholder="Select Qualification"
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="mt-3.5">
            <label htmlFor="google-city" className="mb-1.5 block text-sm font-medium text-slate-700">
              City
            </label>
            <input
              id="google-city"
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              maxLength={80}
              required
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 transition-all focus:border-[#9a51a0] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#9a51a0]/20 sm:rounded-xl"
            />
          </div>

          <div className="mt-3.5 rounded-2xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-xs leading-5 text-slate-600 sm:rounded-xl">
            Signed in as <span className="font-semibold text-slate-800">{user?.email}</span>
          </div>

          <div className="sticky bottom-0 mt-4 bg-gradient-to-t from-white via-white to-white/90 pb-1 pt-3">
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-2xl bg-gradient-to-r from-[#9a51a0] to-[#f36f21] px-4 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:scale-[1.01] hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100 sm:rounded-xl"
            >
              {submitting ? 'Saving Details...' : 'Continue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default GoogleProfileCompletionModal;
